import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import GroupCard from '../components/GroupCard';
import GroupCover from '../components/GroupCover';
import CreateGroupModal from '../components/CreateGroupModal';
import { GROUPS_QUERY, RECOMMENDED_GROUPS_QUERY, DASHBOARD_GROUPS_QUERY } from '../graphql';
import { BROAD_CATEGORIES, PERSONAS, PERSONA_GROUPS, personaById } from '../config/personas';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = ['All','Programming','Art & Design','Music','Gaming','Science','Writing','Business','Sports','Outdoor Activity','Language','Cooking','Gardening','Reading','Dance','Crafts','Photography','Other'];

// Gradient per persona group — themed headers for the Community cards.
const PERSONA_GRADIENT = {
  grehini: 'linear-gradient(135deg,#C05621,#EA580C)',
  expert: 'linear-gradient(135deg,#B45309,#D97706)',
  shopathome: 'linear-gradient(135deg,#38785A,#16A34A)',
  organiser: 'linear-gradient(135deg,#BE185D,#DB2777)',
  media: 'linear-gradient(135deg,#374151,#6B7280)',
  travel: 'linear-gradient(135deg,#0369A1,#0284C7)',
};

function HeroText({ user }) {
  if (!user) return (
    <>
      <h1 style={{fontSize:'var(--font-hero)',fontWeight:800,lineHeight:1.15,marginBottom:12}}>
        Find your people.<br />
        <span style={{background:'linear-gradient(135deg,var(--primary),var(--accent))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
          Build great things together.
        </span>
      </h1>
      <p style={{color:'var(--text-muted)',fontSize:'var(--font-lg)',marginBottom:24,maxWidth:500}}>
        For kids, teens, adults, and seniors — find hobby groups in your building, neighborhood, or city.
      </p>
    </>
  );
  if (user.ageGroup === 'KIDS') return (
    <>
      <h1 style={{fontSize:'var(--font-hero)',fontWeight:800,lineHeight:1.15,marginBottom:12}}>
        Hi {user.name.split(' ')[0]}! 👋<br />
        <span style={{background:'linear-gradient(135deg,var(--primary),var(--accent))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
          Let's find friends & fun! 🎉
        </span>
      </h1>
      <p style={{color:'var(--text-muted)',fontSize:'var(--font-lg)',marginBottom:24}}>
        Find kids near you who love the same things. Join a group, make friends, have fun!
      </p>
    </>
  );
  if (user.ageGroup === 'SENIORS') return (
    <>
      <h1 style={{fontSize:'var(--font-hero)',fontWeight:800,lineHeight:1.15,marginBottom:12}}>
        Welcome, {user.name.split(' ')[0]}!<br />
        <span style={{background:'linear-gradient(135deg,var(--primary),var(--accent))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
          Connect with neighbors &amp; friends
        </span>
      </h1>
      <p style={{color:'var(--text-muted)',fontSize:'var(--font-lg)',marginBottom:24,maxWidth:500}}>
        Find people nearby who share your interests. Join groups, set meeting times, and get reminders.
      </p>
    </>
  );
  return (
    <>
      <h1 style={{fontSize:'var(--font-hero)',fontWeight:800,lineHeight:1.15,marginBottom:12}}>
        Hey {user.name.split(' ')[0]}! 👋<br />
        <span style={{background:'linear-gradient(135deg,var(--primary),var(--accent))',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
          Find your people.
        </span>
      </h1>
      <p style={{color:'var(--text-muted)',fontSize:'var(--font-lg)',marginBottom:24}}>
        Groups of people building, creating, and learning together — nearby and online.
      </p>
    </>
  );
}

export default function Home({ onAuthRequired }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState('All');
  const [broadCat, setBroadCat] = useState('');
  const [persona, setPersona] = useState('');   // "entered" community (from the persona cards)
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [localOnly, setLocalOnly] = useState(false);

  const { data: recData } = useQuery(RECOMMENDED_GROUPS_QUERY, {
    variables: { limit: 5 },
    skip: !currentUser,
  });

  // Browsing = the user is actively searching or filtering → show the full catalog.
  // Otherwise a logged-in user sees their personalized dashboard (joined + skill/location matched).
  const isBrowsing = !!(search || (category && category!=='All') || broadCat || persona || localOnly || filter==='open');
  const dashMode = !!currentUser && !isBrowsing && filter!=='joined';

  const { data: dashData, loading: dashLoading } = useQuery(DASHBOARD_GROUPS_QUERY, {
    variables: { limit: 12 },
    skip: !dashMode,
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });

  const { data, loading: catalogLoading, error } = useQuery(GROUPS_QUERY, {
    variables: {
      category: category==='All'?undefined:category,
      broadCategory: broadCat||undefined,
      persona: persona||undefined,
      search: search||undefined,
      city: localOnly&&currentUser?.location?.city ? currentUser.location.city : undefined,
      building: localOnly&&currentUser?.location?.building ? currentUser.location.building : undefined,
    },
    skip: dashMode,
    pollInterval: 15000,
  });

  const loading = dashMode ? dashLoading : catalogLoading;
  let groups = dashMode ? (dashData?.dashboardGroups || []) : (data?.groups || []);
  if (filter==='open') groups = groups.filter(g=>g.isOpen);
  if (filter==='joined') groups = groups.filter(g=>g.isMember);
  if (currentUser?.ageGroup && filter==='mine') groups = groups.filter(g=>g.ageGroups?.includes(currentUser.ageGroup));

  // Smart categorization: joined > location match > interest match > rest
  if (currentUser && filter === 'all' && !search && category === 'All') {
    const userCity = currentUser?.location?.city?.toLowerCase() || '';
    const userBuilding = currentUser?.location?.building?.toLowerCase() || '';
    const userInterests = (currentUser?.interests || []).map(i => i.toLowerCase());
    groups = [...groups].sort((a, b) => {
      const score = g => {
        if (g.isMember) return 4;
        const gCity = (g.location?.city || '').toLowerCase();
        const gBuilding = (g.location?.building || '').toLowerCase();
        const nearBy = (userBuilding && gBuilding && gBuilding === userBuilding) || (userCity && gCity && gCity === userCity);
        if (nearBy) return 3;
        const tags = [...(g.tags || []), g.category || ''].map(t => t.toLowerCase());
        const hasInterest = userInterests.some(i => tags.some(t => t.includes(i) || i.includes(t)));
        if (hasInterest) return 2;
        return 1;
      };
      return score(b) - score(a);
    });
  }

  const isKids = currentUser?.ageGroup === 'KIDS';
  const isSenior = currentUser?.ageGroup === 'SENIORS';

  return (
    <div className="page">
      {/* Hero */}
      <div style={{background:'linear-gradient(135deg,rgba(192,86,33,0.06),rgba(56,120,90,0.04))',borderBottom:'2px solid var(--border)',padding:'40px 0 28px'}}>
        <div className="container">
          <HeroText user={currentUser} />

          {/* Search + CTA row */}
          <div className="hero-search-row" style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
            <div style={{flex:1,maxWidth:400}}>
              <input className="input" placeholder={isKids?'🔍 Search for something fun…':isSenior?'🔍 Search groups…':'🔍 Search groups…'} value={search} onChange={e=>setSearch(e.target.value)} style={{height:48,fontSize:'var(--font-base)'}} />
            </div>
            {currentUser ? (
              <button className="btn btn-primary btn-lg" onClick={()=>setShowCreate(true)}>
                {isKids?'🎉 Start a Group!':isSenior?'🤝 Start a Group':'✨ Create Group'}
              </button>
            ) : (
              <button className="btn btn-primary btn-lg" onClick={()=>onAuthRequired('register')}>
                Join free — for everyone!
              </button>
            )}
          </div>

          {/* Local toggle — only show if user has location */}
          {currentUser?.location?.city && (
            <div style={{marginTop:14,display:'flex',alignItems:'center',gap:10}}>
              <button onClick={()=>setLocalOnly(!localOnly)}
                style={{display:'flex',alignItems:'center',gap:8,padding:'7px 14px',borderRadius:20,border:`2px solid ${localOnly?'var(--primary)':'var(--border)'}`,background:localOnly?'var(--primary-light)':'transparent',cursor:'pointer',fontWeight:600,fontSize:'var(--font-sm)',color:localOnly?'var(--primary)':'var(--text-muted)',fontFamily:'inherit'}}>
                📍 {localOnly?`My society: ${currentUser.location.building||currentUser.location.city}`:'Search within my society'}
              </button>
              {localOnly && <span style={{fontSize:'var(--font-sm)',color:'var(--text-dim)'}}>Searching only groups in your building / society</span>}
            </div>
          )}
        </div>
      </div>

      <div className="container" style={{padding:'24px 16px'}}>
        {/* Communities — pick a persona card to "Enter" (from the sketch) */}
        {!persona ? (
          <div style={{marginBottom:22}}>
            <h2 style={{fontSize:'var(--font-lg)',fontWeight:800,marginBottom:4}}>🏘️ Communities</h2>
            <p style={{color:'var(--text-muted)',fontSize:'var(--font-sm)',marginBottom:14}}>Step into a community — grehini master chefs, home tutors, sports experts, organisers & more.</p>
            <div className="communities-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))',gap:12}}>
              {PERSONAS.map(p=>(
                <div key={p.id} className="card group-card" style={{padding:0,overflow:'hidden',cursor:'pointer'}}
                  onClick={()=>{setPersona(p.id);window.scrollTo({top:0,behavior:'smooth'});}}>
                  <div style={{height:72,background:PERSONA_GRADIENT[p.group]||'linear-gradient(135deg,var(--primary),var(--accent))',display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <span style={{fontSize:32}}>{p.emoji}</span>
                  </div>
                  <div style={{padding:'10px 12px'}}>
                    <div style={{fontWeight:700,fontSize:13,lineHeight:1.3,marginBottom:2}}>{p.label}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.4}}>{PERSONA_GROUPS[p.group]}</div>
                    <div style={{fontSize:12,fontWeight:700,color:'var(--primary)',marginTop:6}}>Enter →</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="card" style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
            <span style={{fontSize:28}}>{personaById(persona)?.emoji}</span>
            <div style={{flex:1,minWidth:160}}>
              <div style={{fontWeight:800,fontSize:'var(--font-lg)'}}>{personaById(persona)?.label} community</div>
              <div style={{fontSize:'var(--font-sm)',color:'var(--text-muted)'}}>Showing groups in this community</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={()=>setPersona('')}>← All communities</button>
          </div>
        )}

        {/* Broad namespaced categories — agora/<slug> */}
        <div style={{overflowX:'auto',marginBottom:12,paddingBottom:4}}>
          <div style={{display:'flex',gap:8,minWidth:'max-content'}}>
            <button onClick={()=>setBroadCat('')} className="btn btn-sm"
              style={{background:broadCat===''?'var(--primary)':'var(--surface)',color:broadCat===''?'#fff':'var(--text-muted)',border:`2px solid ${broadCat===''?'var(--primary)':'var(--border)'}`,flexShrink:0,fontFamily:'monospace'}}>
              agora/*
            </button>
            {BROAD_CATEGORIES.map(b=>(
              <button key={b.id} onClick={()=>setBroadCat(broadCat===b.id?'':b.id)} className="btn btn-sm"
                style={{background:broadCat===b.id?'var(--primary)':'var(--surface)',color:broadCat===b.id?'#fff':'var(--text-muted)',border:`2px solid ${broadCat===b.id?'var(--primary)':'var(--border)'}`,flexShrink:0,fontFamily:'monospace'}}>
                {b.emoji} agora/{b.slug}
              </button>
            ))}
          </div>
        </div>
        {/* Category scroll */}
        <div style={{overflowX:'auto',marginBottom:16,paddingBottom:4}}>
          <div style={{display:'flex',gap:8,minWidth:'max-content'}}>
            {CATEGORIES.map(c=>(
              <button key={c} onClick={()=>setCategory(c)} className="btn btn-sm"
                style={{background:category===c?'var(--primary)':'var(--surface)',color:category===c?'#fff':'var(--text-muted)',border:`2px solid ${category===c?'var(--primary)':'var(--border)'}`,flexShrink:0}}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Filter row */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:10}}>
          <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
            {[
              {v:'all',l:isSenior?'All Groups':'All'},
              {v:'open',l:'● Open'},
              {v:'joined',l:'✓ Joined'},
              ...(currentUser?[{v:'mine',l:`For ${currentUser.ageGroup==='KIDS'?'Kids':currentUser.ageGroup==='SENIORS'?'Seniors':'Me'}`}]:[]),
            ].map(f=>(
              <button key={f.v} onClick={()=>setFilter(f.v)}
                style={{padding:'7px 14px',borderRadius:20,fontSize:'var(--font-sm)',border:'none',cursor:'pointer',fontFamily:'inherit',fontWeight:600,background:filter===f.v?'var(--primary-light)':'transparent',color:filter===f.v?'var(--primary)':'var(--text-muted)'}}>
                {f.l}
              </button>
            ))}
          </div>
          <span style={{fontSize:'var(--font-sm)',color:'var(--text-dim)'}}>{groups.length} group{groups.length!==1?'s':''}</span>
        </div>

        {/* Personalized dashboard banner */}
        {dashMode && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap', marginBottom:16, padding:'10px 14px', background:'var(--primary-light)', borderRadius:'var(--radius-sm)' }}>
            <span style={{ fontSize:'var(--font-sm)', color:'var(--primary)', fontWeight:600 }}>🏠 Your dashboard — groups you’ve joined + picks matched to your skills & location.</span>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>🔍 Search or pick a category to explore all groups.</span>
          </div>
        )}

        {/* Suggested groups — Meetup-style card grid, skill/location matched */}
        {currentUser && recData?.recommendedGroups?.length > 0 && filter === 'all' && !search && category === 'All' && (
          <div style={{ marginBottom: 30 }}>
            <h2 style={{ fontSize: 'var(--font-lg)', fontWeight: 800, marginBottom: 14 }}>🎯 Suggested groups</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              {recData.recommendedGroups.slice(0, 6).map(r => {
                const g = r.group;
                const loc = [g.location?.city, g.location?.country].filter(Boolean).join(', ');
                return (
                  <div key={g.id} className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
                    onClick={() => navigate(`/group/${g.id}`)}>
                    <GroupCover group={g} height={110} radius="0" />
                    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 'var(--font-base)', lineHeight: 1.3 }}>{g.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{loc ? `${loc} · ` : ''}{g.memberCount} member{g.memberCount === 1 ? '' : 's'}</div>
                      {r.matchReasons?.[0] && <span style={{ fontSize: 11, padding: '2px 8px', background: 'var(--primary-light)', color: 'var(--primary)', borderRadius: 99, fontWeight: 600, alignSelf: 'flex-start' }}>{r.matchReasons[0]}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Groups grid */}
        {loading && !data && <div className="loading-center"><div className="spinner" /></div>}
        {error && (
          <div style={{background:'rgba(239,68,68,0.1)',border:'2px solid rgba(239,68,68,0.3)',borderRadius:'var(--radius)',padding:20,color:'var(--danger)'}}>
            ⚠️ Could not load groups. Please check your connection and try again.
          </div>
        )}
        {!loading && groups.length===0 && (
          <div className="empty-state">
            <div className="empty-state-icon">{isKids?'🌱':'🌟'}</div>
            <div className="empty-state-title">{isKids?'No groups found yet!':'No groups found'}</div>
            <div className="empty-state-desc">{search?`No results for "${search}"`:'Be the first to start one!'}</div>
            {currentUser && <button className="btn btn-primary" style={{marginTop:16}} onClick={()=>setShowCreate(true)}>{isKids?'Start a group! 🎉':'Create a Group'}</button>}
          </div>
        )}
        {!loading && groups.length>0 && (() => {
          // Smart section dividers when using smart sort
          const userCity = currentUser?.location?.city?.toLowerCase() || '';
          const userBuilding = currentUser?.location?.building?.toLowerCase() || '';
          const userInterests = (currentUser?.interests || []).map(i => i.toLowerCase());
          const smartSort = currentUser && filter === 'all' && !search && category === 'All';

          const getSection = g => {
            if (!smartSort) return null;
            if (g.isMember) return 'joined';
            const gCity = (g.location?.city || '').toLowerCase();
            const gBuilding = (g.location?.building || '').toLowerCase();
            const nearBy = (userBuilding && gBuilding && gBuilding === userBuilding) || (userCity && gCity && gCity === userCity);
            if (nearBy) return 'nearby';
            const tags = [...(g.tags || []), g.category || ''].map(t => t.toLowerCase());
            const hasInterest = userInterests.some(i => tags.some(t => t.includes(i) || i.includes(t)));
            if (hasInterest) return 'interests';
            return 'other';
          };

          const SECTION_LABELS = {
            joined: { icon: '✅', label: 'Your Groups', color: 'var(--primary)' },
            nearby: { icon: '📍', label: 'Near You', color: '#10b981' },
            interests: { icon: '💡', label: 'Based on Your Interests', color: '#f97316' },
            other: { icon: '🌐', label: 'Explore More', color: 'var(--text-muted)' },
          };

          let lastSection = null;
          const items = [];
          groups.forEach((g, i) => {
            const sec = getSection(g);
            if (sec && sec !== lastSection) {
              lastSection = sec;
              const { icon, label, color } = SECTION_LABELS[sec];
              items.push(
                <div key={`sec-${sec}`} style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:10,marginTop:i>0?16:0}}>
                  <span style={{fontSize:18}}>{icon}</span>
                  <span style={{fontWeight:700,color,fontSize:'var(--font-base)'}}>{label}</span>
                  <div style={{flex:1,height:2,background:'var(--border)',borderRadius:2}}/>
                </div>
              );
            }
            items.push(<GroupCard key={g.id} group={g} onAuthRequired={()=>onAuthRequired('register')} />);
          });

          return (
            <div className="groups-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
              {items}
            </div>
          );
        })()}
      </div>

      {showCreate && <CreateGroupModal onClose={()=>setShowCreate(false)} />}
    </div>
  );
}
